import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  PackageOpen, 
  MapPin, 
  Navigation, 
  Phone, 
  MessageSquare, 
  PackageCheck,
  Hand,
  CheckCircle2,
  Loader2
} from "lucide-react";
import { getCustomerDisplayName } from "@/lib/formatters";

interface PickupCustomer {
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  phone?: string | null;
}

interface PickupOrder {
  id?: string;
  order_number?: string;
  customer?: PickupCustomer | null;
}

interface PickupCemetery {
  name?: string;
  city?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

export interface PickupData {
  id: string;
  order?: PickupOrder | null;
  cemetery?: PickupCemetery | null;
  latitude?: number;
  longitude?: number;
}

interface PickupCardProps {
  pickup: PickupData;
  onClaim?: () => void;
  onComplete?: () => void;
  onSendSms?: () => void;
  isSmsLoading?: boolean;
  isNotified?: boolean;
  isClaimLoading?: boolean;
  isCompleteLoading?: boolean;
  showClaimButton?: boolean;
  showCompleteButton?: boolean;
  variant?: "compact" | "full";
}

export function PickupCard({
  pickup,
  onClaim,
  onComplete,
  onSendSms,
  isSmsLoading = false,
  isNotified = false,
  isClaimLoading = false,
  isCompleteLoading = false,
  showClaimButton = false,
  showCompleteButton = false,
  variant = "full",
}: PickupCardProps) {
  const customer = pickup.order?.customer;
  const mapsUrl = pickup.cemetery?.latitude && pickup.cemetery?.longitude
    ? `https://www.google.com/maps/search/?api=1&query=${pickup.cemetery.latitude},${pickup.cemetery.longitude}`
    : "";

  // Compact variant for unassigned pickups
  if (variant === "compact") {
    return (
      <div className="flex items-center justify-between p-3 bg-background rounded-lg">
        <div className="min-w-0">
          <div className="font-medium text-sm">{pickup.order?.order_number}</div>
          <div className="text-xs text-muted-foreground truncate">
            {pickup.cemetery?.name}, {pickup.cemetery?.city}
          </div>
        </div>
        {showClaimButton && onClaim && (
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 gap-1"
            onClick={onClaim}
            disabled={isClaimLoading}
          >
            <Hand className="h-3 w-3" />
            Ota
          </Button>
        )}
      </div>
    );
  }

  // Full variant for claimed pickups
  return (
    <Card className="bg-background">
      <CardContent className="p-4">
        <div className="flex gap-3">
          {/* Icon */}
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-orange-500 text-white shrink-0">
            <PackageOpen className="h-5 w-5" />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-medium">{pickup.order?.order_number}</div>
                <div className="text-sm text-muted-foreground">
                  {getCustomerDisplayName(customer)}
                </div>
              </div>
              <Badge variant="outline" className="shrink-0">Nouto</Badge>
            </div>
            
            {/* Location */}
            <div className="mt-2 text-sm">
              <div className="flex items-center gap-1 text-muted-foreground">
                <MapPin className="h-3 w-3" />
                {pickup.cemetery?.name}, {pickup.cemetery?.city}
              </div>
              {pickup.cemetery?.address && (
                <div className="text-xs text-muted-foreground ml-4">
                  {pickup.cemetery.address}
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="mt-3 flex gap-2 flex-wrap">
              {/* Navigate button */}
              {mapsUrl && (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="gap-1">
                    <Navigation className="h-3 w-3" />
                    Navigoi
                  </Button>
                </a>
              )}
              
              {/* Call button */}
              {customer?.phone && (
                <a href={`tel:${customer.phone}`}>
                  <Button variant="outline" size="sm" className="gap-1">
                    <Phone className="h-3 w-3" />
                    Soita
                  </Button>
                </a>
              )}
              
              {/* SMS notification button */}
              {customer?.phone && onSendSms && (
                <Button
                  variant={isNotified ? "ghost" : "outline"}
                  size="sm"
                  className={`gap-1 ${isNotified ? "text-green-600" : ""}`}
                  disabled={isSmsLoading || isNotified}
                  onClick={onSendSms}
                >
                  {isSmsLoading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : isNotified ? (
                    <CheckCircle2 className="h-3 w-3" />
                  ) : (
                    <MessageSquare className="h-3 w-3" />
                  )}
                  {isNotified ? "Ilmoitettu" : "Ilmoita"}
                </Button>
              )}
              
              {/* Complete button */}
              {showCompleteButton && onComplete && (
                <Button 
                  size="sm" 
                  className="gap-1 bg-green-600 hover:bg-green-700"
                  onClick={onComplete}
                  disabled={isCompleteLoading}
                >
                  {isCompleteLoading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <PackageCheck className="h-3 w-3" />
                  )}
                  Noudettu
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
